import i18n from '@/utils/i18n';
import React from 'react';
import { CatalogCarouselItemsLoading } from './catalog-carousel-items-loading';
import { CatalogCarouselItemsMessageRow } from './catalog-carousel-items-message-row';
import {
  CatalogCategoryType,
  ItemListOption,
} from '@/contexts/item-context-types';
import { CatalogItemCarousel } from './catalog-item-carousel';
import { CategoryImage } from './category-image';
import { NULL_CATEGORY_VALUE } from '@/utils/category';
import { StyleSheet } from 'react-native';
import { useCatalogGroupItems } from '@/hooks/use-catalog-group-items';
import { useCatalogLazyCarouselsEnabled } from '@/hooks/dynamic-config/use-catalog-lazy-carousels-enabled';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';

interface Props {
  category: CatalogCategoryType;
  categoryItemCount: number;
  listItems: ItemListOption[];
  onCategoryPress: () => void;
}

const CATEGORY_HEADER_IMAGE_SIZE = 56;

export const CatalogCategoryCarousel: React.FC<Props> = ({
  categoryItemCount,
  category,
  listItems,
  onCategoryPress,
}) => {
  const categoryImagesEnabled = useDynamicConfigValue(
    'category_images_enabled',
  );
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');
  const newItemCardEnabled = useSupplierDynamicConfigValue(
    'card_view_v2_enabled',
    false,
  );

  // Lazy carousels read their items from the store once the list reports them
  // in view. Until then the row keeps its header over a skeleton
  const lazyEnabled = useCatalogLazyCarouselsEnabled();
  const lazy = useCatalogGroupItems(
    lazyEnabled ? category.uuid || NULL_CATEGORY_VALUE : null,
  );

  const ListEmptyComponent = !lazyEnabled ? null : lazy.loading ? (
    <CatalogCarouselItemsLoading testID="catalog-category-carousel-loading" />
  ) : lazy.error ? (
    <CatalogCarouselItemsMessageRow
      message={i18n.t('catalog_category_items_load_error')}
      onRetry={lazy.retry}
      testID="catalog-category-carousel-error"
    />
  ) : (
    <CatalogCarouselItemsMessageRow
      message={i18n.t('no_items_in_this_category')}
      testID="catalog-category-carousel-empty"
    />
  );

  return (
    <CatalogItemCarousel
      count={categoryItemCount}
      HeaderImageComponent={
        categoryImagesEnabled || itemGroupsEnabled ? (
          <CategoryImage
            category={category}
            imageSize={CATEGORY_HEADER_IMAGE_SIZE}
            style={styles.image}
          />
        ) : null
      }
      ListEmptyComponent={ListEmptyComponent}
      listItems={lazyEnabled ? lazy.items : listItems}
      newItemCardEnabled={newItemCardEnabled}
      onSeeAllPress={onCategoryPress}
      title={category.name ?? i18n.t('other')}
    />
  );
};

const styles = StyleSheet.create({
  image: {
    borderRadius: 8,
    height: CATEGORY_HEADER_IMAGE_SIZE,
    width: CATEGORY_HEADER_IMAGE_SIZE,
  },
});
